import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { EntityCard, EntityCardRef, EntityCardProps, BaseEntity } from "components/Entities/EntityCardBase";
import { Chip } from "ui/Chip";
import { Action } from "util/constants";
import { Calendar, Lock } from "lucide-react";
import { formatDateWithoutTimezone } from "config/dayjsConfig";
import api from "config/axiosConfig";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "ui/InputSelect";
import AddButton from "ui/AddButton";
import TargetsModal from "components/Actions/TargetsModal";
import DisplayOnProfilePrompt from "components/User/DisplayOnProfilePrompt";
import { apiRoutes } from "util/apiRoutes";
import PostKindChip from "components/Posts/display/PostKindChip";
import {
  ACTION_TYPE_META,
  type ActionTypeValue,
} from "components/Posts/kindFields/actionTypeMeta";
import ActionDetailPanels, {
  type ActionLocationRecord,
} from "components/Actions/ActionDetailPanels";
import { useActionCardState } from "components/Actions/useActionCardState";
import ContextCommentsSection from "components/Posts/sections/ContextCommentsSection";

interface ActionCardRefactoredProps extends Omit<EntityCardProps<Action>, "entity" | "entityType" | "renderTags" | "renderAdditionalContent" | "renderEditFields" | "renderActions" | "renderPrimaryContent"> {
  action: Action;
  isActionProfilePage?: boolean;
  isEntitySection?: boolean;
  onPrivacyChange?: (value: string) => void;
}

function isActionTypeValue(value: string): value is ActionTypeValue {
  return Object.prototype.hasOwnProperty.call(ACTION_TYPE_META, value);
}

function readActionStringProperty(action: Action, key: string): string | undefined {
  if (!action.properties || typeof action.properties !== "object") {
    return undefined;
  }

  const value = Reflect.get(action.properties, key);
  return typeof value === "string" ? value : undefined;
}

function readActionBooleanProperty(action: Action, key: string): boolean {
  if (!action.properties || typeof action.properties !== "object") {
    return false;
  }

  return Reflect.get(action.properties, key) === true;
}

function readActionType(action: Action): ActionTypeValue | "" {
  const value = readActionStringProperty(action, "type");
  return value && isActionTypeValue(value) ? value : "";
}

function readActionPlaces(action: Action): ActionLocationRecord[] {
  if (!action.properties || typeof action.properties !== "object") {
    return [];
  }

  const value = Reflect.get(action.properties, "places");
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (place): place is ActionLocationRecord => typeof place === "object" && place !== null
  );
}

function readPayloadTargetIds(action: Action): number[] {
  const rawTargets = Reflect.get(action, "targets");
  if (!Array.isArray(rawTargets)) {
    return [];
  }

  return rawTargets.flatMap((target) => {
    if (!target || typeof target !== "object") {
      return [];
    }

    const entityType = Reflect.get(target, "entity_type");
    const entityId = Reflect.get(target, "entity_id");

    if (entityType !== "issue" || typeof entityId !== "number") {
      return [];
    }

    return [entityId];
  });
}

/**
 * Refactored ActionCard using the generic EntityCard component
 */
const ActionCardRefactored = forwardRef<EntityCardRef, ActionCardRefactoredProps>((props, ref) => {
  const {
    action,
    isActionProfilePage = false,
    isEntitySection = false,
    onPrivacyChange,
    ...restProps
  } = props;

  const {
    currentUser,
    isParticipant,
    hasPendingRequest,
    targets,
    setTargets,
    targetsOpen,
    setTargetsOpen,
    profileOpen,
    setProfileOpen,
    selectedPrivacy,
    handleJoinRequest,
    handleCancelParticipation,
    handleCancelRequest,
  } = useActionCardState(action);
  const [editActionType, setEditActionType] = useState<ActionTypeValue | "">(readActionType(action));

  const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN || "";

  useEffect(() => {
    setEditActionType(readActionType(action));
  }, [action]);

  const effectiveActionType = editActionType || readActionType(action);
  const startAt = readActionStringProperty(action, "start_at");
  const isPrivate = readActionBooleanProperty(action, "is_private");
  const places = readActionPlaces(action);
  const targetIds = useMemo(() => {
    const payloadTargetIds = readPayloadTargetIds(action);
    return payloadTargetIds.length > 0 ? payloadTargetIds : targets.map((target) => target.id);
  }, [action, targets]);

  // Render action-specific tags
  const renderTags = (_entity: BaseEntity) => {
    return (
      <>
        <PostKindChip kind="action" />
        {effectiveActionType && (
          <Chip size="sm" className="bg-eoe-gray text-white">
            {ACTION_TYPE_META[effectiveActionType].label}
          </Chip>
        )}
        {typeof startAt === "string" && startAt.length > 0 && (
          <Chip size="sm" icon={<Calendar className="size-3" />} className="bg-eoe-gray text-white">
            <span className="card-date text-xs font-normal text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke">
              {formatDateWithoutTimezone(startAt)}
            </span>
          </Chip>
        )}
        {isPrivate && (
          <Chip size="sm" icon={<Lock className="size-3" />} className="bg-eoe-gray text-white">
            Private
          </Chip>
        )}
        {currentUser && !isParticipant && !hasPendingRequest && (
          <button onClick={handleJoinRequest} className="text-primary-action-text hover:text-primary-action-text-hover text-xs">Join</button>
        )}
        {currentUser && hasPendingRequest && (
          <button onClick={handleCancelRequest} className="text-orange-500 hover:text-orange-700 text-xs">Cancel request</button>
        )}
        {currentUser && isParticipant && action.user_id !== currentUser.id && (
          <button onClick={handleCancelParticipation} className="text-warning-red hover:text-dark-warning-red text-xs">Leave</button>
        )}
      </>
    );
  };

  // Map Action fields to BaseEntity expected by EntityCard
  const mappedEntity: BaseEntity = {
    ...action,
    title: action.title,
    body: action.body,
  };

  // Render additional action-specific content (details, targets, participants) in view mode
  const renderAdditionalContent = (_entity: BaseEntity) => {
    return (
      <>
        <ActionDetailPanels places={places} targetIds={targetIds} imageCdn={IMAGE_CDN} />
        <ContextCommentsSection
          postId={action.id}
          kind="task"
          parentPrivacy={action.properties?.privacy}
          editable={Boolean(props.editMode)}
        />
      </>
    );
  };

  // Render edit-only action fields (type and targets)
  const renderEditFields = (_entity: BaseEntity) => {
    return (
      <div className="form-control mb-2 md:col-span-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="form-control">
            <label className="block text-sm font-semibold">Type of action</label>
            <Select
              value={editActionType || undefined}
              onValueChange={async (val) => {
                if (!isActionTypeValue(val)) {
                  return;
                }

                try {
                  await api.patch(apiRoutes.posts.byId(action.id), { properties: { type: val } });
                  setEditActionType(val);
                } catch {
                  // Keep the previous selection when the patch fails.
                }
              }}
            >
              <SelectTrigger className="input-select px-3 py-2">
                <SelectValue placeholder="Select a type" />
              </SelectTrigger>
              <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main">
                {Object.entries(ACTION_TYPE_META).map(([key, meta]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex flex-col text-left">
                      <span>{meta.label}</span>
                      <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {meta.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="form-control">
            <label className="block text-sm font-semibold">Targets</label>
            <AddButton
              tooltipTitle={targets.length > 0 ? 'Update targeted issues' : 'Add targeted issues'}
              buttonText={targets.length > 0 ? 'Update issues' : 'Issues'}
              onClick={() => { setTargetsOpen(true); }}
              className="w-full"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <EntityCard
        ref={ref}
        entity={mappedEntity}
        entityType="action"
        onPrivacyChange={onPrivacyChange}
        renderTags={renderTags}
        renderAdditionalContent={renderAdditionalContent}
        renderEditFields={renderEditFields}
        hideTitle={!action.title || action.title.trim() === "" || action.title.trim() === "[no_title]"}
        descriptionPlaceholder="Description"
        canEditTitle={false}
        isEntityProfilePage={isActionProfilePage}
        isEntitySection={isEntitySection}
        {...restProps}
      />
      <DisplayOnProfilePrompt
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        entityType="action"
        entityId={Number(action.id)}
        defaultPrivacy={selectedPrivacy}
      >
        <ActionCardRefactored action={action} isActionProfilePage={false} isEntitySection={true} />
      </DisplayOnProfilePrompt>
      {restProps.editMode && (
        <TargetsModal
          isOpen={targetsOpen}
          initialTargets={targets.map(t => ({ id: t.id }))}
          onClose={() => setTargetsOpen(false)}
          onSave={async (list) => {
            try {
              const payload = { targets: list.map(i => ({ entity_id: i.id, entity_type: 'issue' })) };
              await api.patch(apiRoutes.posts.byId(action.id), payload);
              setTargets(list.map(i => ({ id: i.id })));
            } catch (e) { /* ignore */ }
          }}
        />
      )}
    </>
  );
});

ActionCardRefactored.displayName = "ActionCardRefactored";

export default ActionCardRefactored;


